import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import attendanceAnalyticsService from '../../../services/attendanceAnalyticsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const AttendanceAnalytics = ({ isAdmin, clientId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    department: ''
  });

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  useEffect(() => {
    loadAnalytics();
  }, [filters, clientId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const analyticsFilters = { ...filters };
      if (clientId) {
        analyticsFilters.client_id = clientId;
      }
      const result = await attendanceAnalyticsService.getAnalytics(analyticsFilters);
      if (result?.success) {
        setAnalytics(result.data);
      } else {
        setNotification({ message: result?.error || 'Failed to load analytics', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !analytics) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground mt-2">Loading analytics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <Icon name="BarChart3" size={48} className="mx-auto mb-4 opacity-50" />
        <p>No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Attendance Analytics</h4>
          <p className="text-sm text-muted-foreground">Comprehensive attendance insights and trends</p>
        </div>
        <div className="flex gap-2">
          <Input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="w-auto"
          />
          <Input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="w-auto"
          />
          <Button variant="outline" onClick={loadAnalytics}>
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {analytics.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Overall Attendance</p>
            <p className="text-2xl font-bold text-foreground">
              {analytics.summary.overall_attendance_percentage || 0}%
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Present Days</p>
            <p className="text-2xl font-bold text-green-600">
              {analytics.summary.present_count || 0}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Late Arrivals</p>
            <p className="text-2xl font-bold text-orange-600">
              {analytics.summary.late_count || 0}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">Absent Days</p>
            <p className="text-2xl font-bold text-red-600">
              {analytics.summary.absent_count || 0}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Camera" size={16} className="text-primary" />
              <p className="text-sm text-muted-foreground">Selfie Attendance</p>
            </div>
            <p className="text-2xl font-bold text-primary">
              {analytics.summary.selfie_attendance_count || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.summary.total_attendance_count > 0 
                ? `${Math.round((analytics.summary.selfie_attendance_count || 0) / analytics.summary.total_attendance_count * 100)}% adoption`
                : '0% adoption'}
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="MapPin" size={16} className="text-success" />
              <p className="text-sm text-muted-foreground">Location Verified</p>
            </div>
            <p className="text-2xl font-bold text-success">
              {analytics.summary.location_verified_count || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {analytics.summary.selfie_attendance_count > 0
                ? `${Math.round((analytics.summary.location_verified_count || 0) / analytics.summary.selfie_attendance_count * 100)}% verified`
                : '0% verified'}
            </p>
          </div>
        </div>
      )}

      {/* Monthly Trend Chart */}
      {analytics.monthly_trend && analytics.monthly_trend.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h5 className="text-lg font-semibold text-foreground mb-4">Monthly Attendance Trend</h5>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="present_count" stroke="#10B981" name="Present" />
                <Line type="monotone" dataKey="late_count" stroke="#F59E0B" name="Late" />
                <Line type="monotone" dataKey="absent_count" stroke="#EF4444" name="Absent" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Department Stats */}
      {analytics.department_stats && analytics.department_stats.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h5 className="text-lg font-semibold text-foreground mb-4">Department-wise Statistics</h5>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.department_stats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="present_count" fill="#10B981" name="Present" />
                <Bar dataKey="late_count" fill="#F59E0B" name="Late" />
                <Bar dataKey="absent_count" fill="#EF4444" name="Absent" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Late Arrival Analysis */}
      {analytics.late_arrival_analysis && analytics.late_arrival_analysis.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h5 className="text-lg font-semibold text-foreground mb-4">Late Arrival Analysis</h5>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.late_arrival_analysis}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" label={{ value: 'Hour', position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="late_count" fill="#F59E0B" name="Late Count" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Absentees */}
      {analytics.top_absentees && analytics.top_absentees.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h5 className="text-lg font-semibold text-foreground mb-4">Top Absentees</h5>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Employee</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Department</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Absent Days</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Absence %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {analytics.top_absentees.map((employee, index) => (
                  <tr key={employee.id || index} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">{employee.full_name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{employee.department}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{employee.absent_count}</td>
                    <td className="px-4 py-3 text-sm text-red-600 font-medium">
                      {employee.absence_percentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Selfie Attendance Adoption */}
      {analytics.selfie_adoption && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h5 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Camera" size={20} className="text-primary" />
            Selfie Attendance Adoption
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {analytics.selfie_adoption.method_comparison && (
              <div>
                <h6 className="text-sm font-medium text-muted-foreground mb-3">Attendance Methods</h6>
                <div className="w-full h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics.selfie_adoption.method_comparison}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.selfie_adoption.method_comparison.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {analytics.selfie_adoption.daily_trend && analytics.selfie_adoption.daily_trend.length > 0 && (
              <div>
                <h6 className="text-sm font-medium text-muted-foreground mb-3">Daily Selfie Usage Trend</h6>
                <div className="w-full h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.selfie_adoption.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="selfie_count" stroke="#3B82F6" name="Selfie Attendance" strokeWidth={2} />
                      <Line type="monotone" dataKey="total_count" stroke="#10B981" name="Total Attendance" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
          {analytics.selfie_adoption.department_adoption && analytics.selfie_adoption.department_adoption.length > 0 && (
            <div className="mt-6">
              <h6 className="text-sm font-medium text-muted-foreground mb-3">Department-wise Selfie Adoption</h6>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Department</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Selfie Count</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Total Count</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Adoption Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {analytics.selfie_adoption.department_adoption.map((dept, index) => (
                      <tr key={dept.department || index} className="hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm text-foreground">{dept.department || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{dept.selfie_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{dept.total_count || 0}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`font-medium ${
                            (dept.adoption_rate || 0) >= 80 ? 'text-green-600' :
                            (dept.adoption_rate || 0) >= 50 ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {dept.adoption_rate || 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Location Verification Stats */}
      {analytics.location_stats && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h5 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="MapPin" size={20} className="text-success" />
            Location Verification Statistics
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Verified</p>
              <p className="text-2xl font-bold text-success">
                {analytics.location_stats.verified_count || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics.location_stats.total_with_location > 0
                  ? `${Math.round((analytics.location_stats.verified_count || 0) / analytics.location_stats.total_with_location * 100)}% of location records`
                  : '0%'}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Average Accuracy</p>
              <p className="text-2xl font-bold text-primary">
                ±{analytics.location_stats.avg_accuracy ? Math.round(analytics.location_stats.avg_accuracy) : 0}m
              </p>
              <p className="text-xs text-muted-foreground mt-1">GPS accuracy</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Geo-fence Matches</p>
              <p className="text-2xl font-bold text-success">
                {analytics.location_stats.geo_fence_matches || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics.location_stats.verified_count > 0
                  ? `${Math.round((analytics.location_stats.geo_fence_matches || 0) / analytics.location_stats.verified_count * 100)}% match rate`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Punctuality Scores */}
      {analytics.punctuality_scores && analytics.punctuality_scores.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h5 className="text-lg font-semibold text-foreground mb-4">Punctuality Scores</h5>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Employee</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Department</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">On Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Late</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Punctuality Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {analytics.punctuality_scores.map((employee, index) => (
                  <tr key={employee.id || index} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">{employee.full_name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{employee.department}</td>
                    <td className="px-4 py-3 text-sm text-green-600">{employee.on_time_count}</td>
                    <td className="px-4 py-3 text-sm text-orange-600">{employee.late_count}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`font-medium ${
                        employee.punctuality_score >= 90 ? 'text-green-600' :
                        employee.punctuality_score >= 70 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {employee.punctuality_score}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceAnalytics;

